import Segment from './SegmentDto';
import {ISegmentListDto} from './SegmentsListDtoInterface';
import {generateUniqueString} from '../../../../../utils/random';

class SegmentListDto implements ISegmentListDto {
  id: string;
  segments: Segment[];

  static MIN_VALUE = 0;
  static INCREMENTS = 1;
  static MAX_VALUE = 100;
  static MIN_SEGMENT_LENGTH = 5;

  constructor(segments: Segment[] = []) {
    this.id = generateUniqueString();
    this.segments = this.sortSegmentsByStartingPoint(segments);
  }

  addSegment(segment: Segment): boolean {
    let isSegmentAdded = false;

    if (this.canAddSegment(segment)) {
      this.segments.push(segment);
      this.segments = this.sortSegmentsByStartingPoint(this.segments);
      isSegmentAdded = true;
    }

    return isSegmentAdded;
  }

  removeSegment(id: string): boolean {
    const initialSegmentsQuantity = this.segments.length;
    this.segments = this.segments.filter(segment => segment.id !== id);
    return this.segments.length < initialSegmentsQuantity;
  }

  updateSegment(id: string, newStart: number, newEnd: number): boolean {
    let missionAccomplished = false;
    const segment = this.getSegmentById(id);

    if (segment) {
      const updatedSegment = segment.clone();

      updatedSegment.resize(newStart, newEnd);

      if (
        this.isValidSegmentLength(updatedSegment) &&
        this.canUpdateSegmentWithoutOverlapings(segment, updatedSegment)
      ) {
        segment.resize(newStart, newEnd);
        this.segments = this.sortSegmentsByStartingPoint(this.segments);
        missionAccomplished = true;
      }
    }

    return missionAccomplished;
  }

  private sortSegmentsByStartingPoint(segments: Segment[]): Segment[] {
    return segments.sort((a, b) => a.start - b.start);
  }

  private canAddSegment(newSegment: Segment): boolean {
    const isSegmentInBounds =
      newSegment.start >= SegmentListDto.MIN_VALUE &&
      newSegment.end <= SegmentListDto.MAX_VALUE;
    const isSegmentValid = newSegment.start <= newSegment.end;
    const isSegmentNotOverlapping = !this.segments.some(segment =>
      segment.overlaps(newSegment)
    );

    const isZeroLengthSegment = newSegment.length === 0;
    const isUniqueZeroLengthSegment = isZeroLengthSegment
      ? !this.segments.some(segment => segment.length === 0)
      : true;

    return (
      isSegmentInBounds &&
      isSegmentValid &&
      isSegmentNotOverlapping &&
      isUniqueZeroLengthSegment
    );
  }

  private isValidSegmentLength(segment: Segment): boolean {
    return segment.length >= SegmentListDto.MIN_SEGMENT_LENGTH;
  }

  private canUpdateSegmentWithoutOverlapings(
    oldSegment: Segment,
    updatedSegment: Segment
  ): boolean {
    return this.segments.every(
      segment => segment === oldSegment || !segment.overlaps(updatedSegment)
    );
  }

  getAllSegments(): Segment[] {
    return [...this.segments];
  }

  getAllValidSegments(): ISegmentListDto {
    return new SegmentListDto(
      this.segments.filter(segment => this.isValidSegmentLength(segment))
    );
  }

  getSegmentById(id: string): Segment | undefined {
    return this.segments.find(segment => segment.id === id);
  }

  getInitialSegment(): Segment {
    return this.segments[0];
  }

  getLastSegment(): Segment {
    return this.segments[this.segments.length - 1];
  }

  mergeAdjacentSegments(): number {
    let mergeCount = 0;

    for (let i = 0; i < this.segments.length - 1; i++) {
      const mergedSuccessfully = this.segments[i].merge(this.segments[i + 1]);

      if (mergedSuccessfully) {
        this.segments.splice(i + 1, 1);

        mergeCount++;

        i--;
      }
    }

    return mergeCount;
  }

  splitSegment(id: string, splitPoint: number): boolean {
    let splitSuccessful = false;
    const segmentIndex = this.segments.findIndex(segment => segment.id === id);

    if (segmentIndex !== -1) {
      const newSegment = this.segments[segmentIndex].split(splitPoint);

      this.segments.splice(segmentIndex + 1, 0, newSegment);

      splitSuccessful = true;
    }

    return splitSuccessful;
  }

  getTotalDuration(): number {
    return this.segments.reduce((total, segment) => total + segment.length, 0);
  }

  isPointInAnySegment(point: number): boolean {
    return this.segments.some(segment => segment.containsPoint(point));
  }

  toString(): string {
    return `SegmentList: [${this.segments
      .map(segment => segment.toString())
      .join(', ')}]`;
  }
}

export default SegmentListDto;
